"use client";

import { useCallback } from "react";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import Heading from "~/componentsDS/Typography/Heading";
import Accordion, { type FAQ } from "~/componentsDS/Accordion";
import Overline from "~/componentsDS/Typography/Overline";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import type { GtmEvent } from "~/lib/gtm/getGtmEventData";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import { TREATMENTS_AND_SAFETY, GETTING_PRESCRIBED, ELIGIBILITY_AND_ACCESS, SHIPPING_AND_DELIVERY, PRODUCT_USE } from "~/componentsDS/LPComponents/data";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import FAQSchema from "~/components/Schema/FAQSchema";
import { cn } from "~/lib/utils";

type FAQGroup = {
    title: string;
    items: FAQ[];
    accordionClassName?: string;
};

type Props = {
    eventDataOnOpen?: Omit<GtmEvent["value"], "meta_data">;
    groups?: FAQGroup[];
    sectionClassName?: string;
};

const defaultFaqGroups: FAQGroup[] = [
    { title: "TREATMENTS & SAFETY", items: TREATMENTS_AND_SAFETY, accordionClassName: "whitespace-pre-line mb-8" },
    { title: "GETTING PRESCRIBED", items: GETTING_PRESCRIBED, accordionClassName: "mb-8" },
    { title: "PRODUCT USE", items: PRODUCT_USE, accordionClassName: "mb-8" },
    { title: "ELIGIBILITY & ACCESS", items: ELIGIBILITY_AND_ACCESS, accordionClassName: "mb-8" },
    { title: "SHIPPING & DELIVERY", items: SHIPPING_AND_DELIVERY, accordionClassName: "" },
];

export const FAQSection = ({ eventDataOnOpen, groups = defaultFaqGroups, sectionClassName }: Props) => {
    const sendEvent = useTrackEvent();

    const handleOpen = useCallback((item: FAQ) => {
        const resolvedEventValue: GtmEvent["value"] =
            eventDataOnOpen
                ? {
                    ...eventDataOnOpen,
                    meta_data: {
                        click_text: item.question,
                    },
                }
                : {
                    event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                    widget: "faqs",
                    meta_data: {
                        click_text: item.question,
                    },
                };

        sendEvent({
            event: "user_interaction",
            value: resolvedEventValue,
        });
    }, [eventDataOnOpen, sendEvent]);

    return (
        <section id="faq-section" className={cn("relative z-10 -mt-5 pt-16 pb-8 lg:py-24 bg-softBlue rounded-t-[24px]", sectionClassName)}>
            <ElementorContainer innerClassName="flex flex-col md:flex-row items-start gap-10 text-primary500">
                <div className="xl:w-[440px] max-xl:text-center self-center lg:self-start">
                    <Heading size="md" className="font-suisseBP">
                        Questions?
                    </Heading>
                </div>

                <div className="xl:w-1/2 grow">
                    {groups.map(({ title, items, accordionClassName }) => (
                        <div key={title}>
                            <Overline className="text-primary300 mb-4 max-xl:text-center" weight="500">
                                {title}
                            </Overline>
                            <Accordion
                                className={accordionClassName || undefined}
                                variant="condensed_v2"
                                items={items}
                                onOpen={handleOpen}
                                renderQuestion={(question) => (
                                    <TextWithRegisteredMark className="text-[18px]" text={question} />
                                )}
                            />
                        </div>
                    ))}
                </div>
            </ElementorContainer>

            <FAQSchema items={groups.flatMap((group) => group.items)} />
        </section>
    );
};

export default FAQSection;
